import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../notification/notification.service';
import { Carrier } from '../../../../backend/src/carriers/entities/carrier.entity';
import { PreferredCarrier } from '../../../../backend/src/carriers/entities/preferred-carrier.entity';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';

/**
 * Szállítók — Stokker design (06-szallitok.html) a valós API-ra igazítva:
 * - szállító pill-tabok (GET carriers), törlés a kiválasztott tabon (DELETE carriers/:id)
 * - név / URL inline szerkesztés (POST carriers)
 * - "Kiszolgált országok" chipek = ország→szállító preferenciák
 *   (GET/POST/DELETE carriers/preferred)
 * - címke-beállítások kártya (GET|PATCH integrations/label-settings)
 */
@Component({
  selector: 'app-carriers',
  templateUrl: './carriers.component.html',
  styleUrl: './carriers.component.scss'
})
export class CarriersComponent implements OnInit {

  carriers: Carrier[] = [];

  // --- Súly-alapú díjszabás (zónák + súlysávok) ---
  zones: any[] = [];
  loadingZones = false;
  savingRate = false;
  newZoneName = '';
  newZoneCountries = '';
  /** próba-kalkuláció a szerkesztő alján */
  testCountry = 'US';
  testWeight = 250;
  testQuote: any = null;
  fetchingCarriers = false;
  selectedId: number | null = null;

  preferred: PreferredCarrier[] = [];
  fetchingPreferred = false;

  // Kiválasztott szállító szerkeszthető mezői (design: carName / carSetName)
  nameDraft = '';
  urlDraft = '';
  savingCarrier = false;

  // Ország hozzáadó input (design: carCountryInput)
  countryInput = '';
  addingCountry = false;

  labelSettings: { default_label_type: string; usa_label_type: string } = { default_label_type: 'A4', usa_label_type: 'A6' };
  savingLabelSettings = false;

  editorOpen = false;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService
  ) { }

  ngOnInit(): void {
    this.fetchCarriers();
    this.fetchPreferred();
    this.fetchLabelSettings();
  }

  // --- Lekérdezések -------------------------------------------------------

  fetchCarriers(selectId?: number) {
    this.fetchingCarriers = true;
    this.http.get<Carrier[]>('carriers').subscribe((carriers) => {
      this.carriers = carriers || [];
      this.fetchingCarriers = false;
      const wanted = selectId ?? this.selectedId;
      const found = this.carriers.find(c => c.id === wanted) || this.carriers[0] || null;
      this.selectedId = found?.id ?? null;
      this.syncDrafts();
      // A díjszabást is töltsük be a kiválasztott szállítóhoz.
      this.fetchZones();
    }, e => {
      this.fetchingCarriers = false;
      this.notificationService.show(`Hiba a szállítók betöltése során! Részletek: ${e.message}`, 'danger');
    });
  }

  fetchPreferred() {
    this.fetchingPreferred = true;
    this.http.get<PreferredCarrier[]>('carriers/preferred').subscribe((preferred) => {
      this.preferred = preferred || [];
      this.fetchingPreferred = false;
    }, e => {
      this.fetchingPreferred = false;
      this.notificationService.show(`Hiba a preferált szállítók betöltése során! Részletek: ${e.message}`, 'danger');
    });
  }

  fetchLabelSettings() {
    this.http.get<{ default_label_type: string; usa_label_type: string }>('integrations/label-settings').subscribe((settings) => {
      this.labelSettings = settings;
    }, e => {
      this.notificationService.show(`Hiba a label típus beállítások betöltésekor! Részletek: ${e.message}`, 'warning');
    });
  }

  // --- Kiválasztás --------------------------------------------------------

  get selectedCarrier(): Carrier | null {
    return this.carriers.find(c => c.id === this.selectedId) || null;
  }

  /** A kiválasztott szállítóhoz rendelt országok (preferenciák). */
  get selectedCountries(): PreferredCarrier[] {
    if (this.selectedId == null) {
      return [];
    }
    return this.preferred.filter(p => p.carrier_id === this.selectedId);
  }

  selectCarrier(carrier: Carrier) {
    this.selectedId = carrier.id ?? null;
    this.countryInput = '';
    this.syncDrafts();
    this.fetchZones();
  }

  // -------------------------------------------------------------------------
  // Súly-alapú díjszabás (zónák + súlysávok)
  // -------------------------------------------------------------------------

  /** A kiválasztott szállító zónái a súlysávjaikkal. */
  fetchZones() {
    if (!this.selectedId) {
      this.zones = [];
      return;
    }
    this.loadingZones = true;
    this.http.get<any[]>(`carriers/shipping-zones?carrier=${this.selectedId}`).subscribe({
      next: (list) => {
        this.zones = (list || []).map((z) => ({
          ...z,
          countryText: (z.country_codes || []).join(', '),
          rates: (z.rates || []).slice().sort((a: any, b: any) => a.max_weight_grams - b.max_weight_grams),
          newWeight: null,
        }));
        this.loadingZones = false;
      },
      error: () => { this.zones = []; this.loadingZones = false; },
    });
  }

  addZone() {
    const name = (this.newZoneName || '').trim();
    if (!name || !this.selectedId) {
      this.notificationService.show('A zóna nevét add meg.', 'danger');
      return;
    }
    this.http.post('carriers/shipping-zones', {
      carrier_id: this.selectedId,
      name,
      country_codes: (this.newZoneCountries || '').split(/[,;\s]+/).filter(Boolean),
      priority: 100,
    }).subscribe({
      next: () => {
        this.newZoneName = '';
        this.newZoneCountries = '';
        this.notificationService.show('Zóna létrehozva.', 'success');
        this.fetchZones();
      },
      error: (e) => this.notificationService.show(e?.error?.message || 'A zóna létrehozása nem sikerült!', 'danger'),
    });
  }

  /** Zóna neve / országlistája / gyűjtő-jelölése mentése. */
  saveZone(zone: any) {
    this.http.patch(`carriers/shipping-zones/${zone.id}`, {
      name: zone.name,
      country_codes: (zone.countryText || '').split(/[,;\s]+/).filter(Boolean),
      is_fallback: !!zone.is_fallback,
      priority: zone.priority,
    }).subscribe({
      // Csendes mentés: gépelés közben ne ugráljon fel értesítés — csak a
      // sor melletti „mentve" jelzés villan fel.
      next: () => this.flashSaved(zone),
      error: (e) => this.notificationService.show(e?.error?.message || 'A zóna mentése nem sikerült!', 'danger'),
    });
  }

  /** Rövid „mentve" visszajelzés a zóna fejlécében (toast helyett). */
  private flashSaved(target: any): void {
    target.savedFlash = true;
    setTimeout(() => { target.savedFlash = false; }, 1500);
  }

  removeZone(zone: any) {
    this.confirmDialog.open({
      title: 'Zóna törlése?',
      message: `A(z) „${zone.name}" zóna és a hozzá tartozó súlysávok törlődnek.`,
      tone: 'danger',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.http.delete(`carriers/shipping-zones/${zone.id}`).subscribe({
        next: () => { this.notificationService.show('Zóna törölve.', 'success'); this.fetchZones(); },
        error: (e) => this.notificationService.show(e?.error?.message || 'A törlés nem sikerült!', 'danger'),
      });
    });
  }

  /** Egy súlysáv mentése (új vagy meglévő). */
  /**
   * Díjsáv mentése. Csendben megy (nincs felugró értesítés), és NEM tölti újra
   * az egész listát — így a szerkesztés közben nem ugrik el a fókusz.
   */
  saveRate(zone: any, rate: any) {
    this.savingRate = true;
    this.http.post<any>(`carriers/shipping-zones/${zone.id}/rates`, {
      id: rate.id,
      max_weight_grams: rate.max_weight_grams,
      shipping_fee: rate.shipping_fee,
      customs_fee: rate.customs_fee,
      surcharge_percent: rate.surcharge_percent,
    }).subscribe({
      next: (saved) => {
        this.savingRate = false;
        // Új sávnál megkapjuk az id-t, hogy a következő mentés már frissítsen.
        if (saved?.id && !rate.id) {
          rate.id = saved.id;
        }
        this.flashSaved(rate);
      },
      error: (e) => {
        this.savingRate = false;
        this.notificationService.show(e?.error?.message || 'A díjsáv mentése nem sikerült!', 'danger');
      },
    });
  }

  addRate(zone: any) {
    const weight = Math.round(Number(zone.newWeight));
    if (!Number.isFinite(weight) || weight <= 0) {
      this.notificationService.show('Adj meg pozitív súlyhatárt (grammban).', 'danger');
      return;
    }
    this.http.post(`carriers/shipping-zones/${zone.id}/rates`, {
      max_weight_grams: weight, shipping_fee: 0, customs_fee: 0, surcharge_percent: 0,
    }).subscribe({
      next: () => { zone.newWeight = null; this.fetchZones(); },
      error: (e) => this.notificationService.show(e?.error?.message || 'A díjsáv létrehozása nem sikerült!', 'danger'),
    });
  }

  removeRate(zone: any, rate: any) {
    this.http.delete(`carriers/shipping-rates/${rate.id}`).subscribe({
      next: () => { zone.rates = (zone.rates || []).filter((r: any) => r.id !== rate.id); },
      error: (e) => this.notificationService.show(e?.error?.message || 'A törlés nem sikerült!', 'danger'),
    });
  }

  /** Próba-kalkuláció: ország + súly → melyik zóna, melyik sáv, mennyi. */
  runTestQuote() {
    const country = (this.testCountry || '').trim().toUpperCase();
    const weight = Number(this.testWeight) || 0;
    if (!country) {
      return;
    }
    this.http.get<any>(`carriers/shipping-quote?country=${country}&weight=${weight}&carrier=${this.selectedId}`)
      .subscribe({
        next: (q) => { this.testQuote = q; },
        error: () => { this.testQuote = null; },
      });
  }

  /** Egy sáv nettó összesenje (a táblázat utolsó oszlopa). */
  rateTotal(rate: any): number {
    const ship = Number(rate?.shipping_fee) || 0;
    const customs = Number(rate?.customs_fee) || 0;
    const pct = Number(rate?.surcharge_percent) || 0;
    return Math.round((ship + customs + ship * pct / 100) * 100) / 100;
  }

  private syncDrafts() {
    const carrier = this.selectedCarrier;
    this.nameDraft = carrier?.name || '';
    this.urlDraft = carrier?.url || '';
  }

  trackByCarrier(_: number, carrier: Carrier) {
    return carrier.id;
  }

  trackByPreferred(_: number, preferred: PreferredCarrier) {
    return preferred.id;
  }

  // --- Szállító CRUD ------------------------------------------------------

  addCarrier() {
    this.editorOpen = true;
  }

  closeEditor() {
    this.editorOpen = false;
  }

  onCarrierSaved(saved: Carrier) {
    this.editorOpen = false;
    this.fetchCarriers(saved?.id);
  }

  /** Név / URL inline mentés (change = blur vagy Enter, mint a prototípus onChange). */
  saveCarrierDetails() {
    const carrier = this.selectedCarrier;
    if (!carrier || this.savingCarrier) {
      return;
    }
    const name = this.nameDraft.trim();
    const url = this.urlDraft.trim();
    if (name.length < 2) {
      this.notificationService.show('A szállító neve legalább 2 karakter legyen!', 'warning');
      this.syncDrafts();
      return;
    }
    if (name === (carrier.name || '') && url === (carrier.url || '')) {
      return;
    }
    this.savingCarrier = true;
    this.http.post<Carrier>('carriers', { id: carrier.id, name, url }).subscribe(() => {
      carrier.name = name;
      carrier.url = url;
      this.savingCarrier = false;
      this.notificationService.show('Szállító sikeresen mentve!', 'success');
    }, e => {
      this.savingCarrier = false;
      this.notificationService.show(`Hiba a szállító mentése során! Részletek: ${e.message}`, 'danger');
    });
  }

  removeCarrier(carrier: Carrier, event: Event) {
    event.stopPropagation();
    this.confirmDialog.open({
      title: 'Szállító törlése',
      message: `A(z) „${carrier.name}” szállító törlődik.`,
      details: 'A hozzá rendelt ország-preferenciák érvényüket vesztik. A művelet nem vonható vissza.',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
      tone: 'danger'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.http.delete(`carriers/${carrier.id}`).subscribe(() => {
        this.notificationService.show('Szállító sikeresen törölve!', 'success');
        this.selectedId = null;
        this.fetchCarriers();
        this.fetchPreferred();
      }, e => {
        this.notificationService.show(`Hiba a szállító törlése során! Részletek: ${e.message}`, 'danger');
      });
    });
  }

  // --- Országok (preferenciák) --------------------------------------------

  onCountryInput(value: string) {
    this.countryInput = (value || '').toUpperCase();
  }

  addCountry() {
    const carrier = this.selectedCarrier;
    if (!carrier || this.addingCountry) {
      return;
    }
    const code = this.countryInput.trim().toUpperCase();
    if (!code) {
      return;
    }
    if (!/^[A-Z]{2}$/.test(code)) {
      this.notificationService.show('Kétbetűs országkódot adj meg (pl. DE).', 'warning');
      return;
    }
    const existing = this.preferred.find(p => (p.country_iso || '').toUpperCase() === code);
    if (existing) {
      if (existing.carrier_id === carrier.id) {
        // A prototípus a duplikátumot csendben elengedi, csak az inputot üríti.
        this.countryInput = '';
        return;
      }
      this.notificationService.show(`A(z) ${code} országhoz már a(z) ${existing.carrier?.name || 'másik'} szállító van beállítva.`, 'warning');
      return;
    }
    this.addingCountry = true;
    this.http.post<PreferredCarrier>('carriers/preferred', { carrier_id: carrier.id, country_iso: code }).subscribe(() => {
      this.addingCountry = false;
      this.countryInput = '';
      this.notificationService.show('Preferencia sikeresen mentve!', 'success');
      this.fetchPreferred();
    }, e => {
      this.addingCountry = false;
      this.notificationService.show(`Hiba a preferencia mentése során! Részletek: ${e.message}`, 'danger');
    });
  }

  removeCountry(preferred: PreferredCarrier) {
    this.http.delete(`carriers/preferred/${preferred.id}`).subscribe(() => {
      this.preferred = this.preferred.filter(p => p.id !== preferred.id);
      this.notificationService.show('Preferált szállító sikeresen törölve!', 'success');
    }, e => {
      this.notificationService.show(`Hiba a preferált szállító törlése során! Részletek: ${e.message}`, 'danger');
    });
  }

  // --- Címke-beállítások ----------------------------------------------------

  saveLabelSettings() {
    this.savingLabelSettings = true;
    this.http.patch('integrations/label-settings', this.labelSettings).subscribe(() => {
      this.savingLabelSettings = false;
      this.notificationService.show('Label típus beállítások mentve.', 'success');
    }, e => {
      this.savingLabelSettings = false;
      this.notificationService.show(`Hiba a label típus beállítás mentésekor! Részletek: ${e.message}`, 'danger');
    });
  }

}
